import { PrimaryButton } from "@kalyzee/kalyzee-ui";
import { MeetingOutputDTO, ROLE, SORT_ORDER, TimeEventOutputDTO, TimeEventQueryMeetingPopulate, TimeEventQueryPopulate, TimeEventSortMethod, TimeEventTargetModels } from "@kalyzee/kast-web-sdk/http";
import { SearchInput } from "@kalyzee/kast-web-sdk/web-components";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { PageMasterMode, usePageContextRef } from "../../../../components/navigation/PageContext";
import { ErrorView } from "../../../../components/utils/ErrorView";
import { TimeEventsAgenda } from "../../../../components/utils/TimeEventsAgenda";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { useSdkContext } from "../../../../contexts/sdk/context";
import { isRoleLessThan } from "../../../../helpers/roles";
import { useDelayedState } from "../../../../hooks/states";
import { useSettings } from "../../../../hooks/settings";
import styles from "./list.module.css";

export const VodsAgenda = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();
  const isReadyRef = useRef(false);

  const user = useAppStoreSelector(state => state.user);

  const [selectedEventId, setSelectedEventId] = useState<string>();
  const [settings] = useSettings();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [search, setSearch] = useState<string>();

  const defaultResult = useMemo(() => ({ fetching: false, events: undefined }), []);
  const [result, setResult] = useDelayedState<{ fetching: boolean, events: TimeEventOutputDTO[] | undefined }>(defaultResult);
  const [error, setError] = useState<any>();

  const sdkCtx = useSdkContext();

  const onPlayVod = (eventId: string) => {
    const selectedEvent = result.events?.find((event) => event.id === eventId);
    const selectedMeeting = selectedEvent ? (selectedEvent.target as MeetingOutputDTO) : undefined;
    if (selectedMeeting) {
      pageContextRef.current?.goTo(
        PageMasterMode.LIST,
        undefined,
        { queries: { vodId: selectedMeeting.id } },
      );
    }
  };

  const loadEvents = useCallback(() => {
    const afterDate = dateRange[0];
    let beforeDate = dateRange[1] ?? afterDate;
    if (beforeDate) {
      beforeDate = new Date(beforeDate.getFullYear(), beforeDate.getMonth(), beforeDate.getDate(), 23, 59, 59);
    }
    setResult(prev => ({ ...prev, fetching: true }));
    sdkCtx.client
      .getHttpClient()
      .timeEvent.getAll({
        rooms: undefined,
        targets: [TimeEventTargetModels.Meeting],
        meetingFilter: {
          containsActors: user && isRoleLessThan(user.role, ROLE.TECHNICIAN) ? [user.id] : undefined,
          record: true,
          search: search ? { value: search, fields: ["name", "moderator"] } : undefined,
        },
        populate: settings.dev ? [TimeEventQueryPopulate.ROOM] : [],
        meetingPopulate: [TimeEventQueryMeetingPopulate.ACTOR_IDENTITY],
        afterDate: afterDate ?? undefined,
        beforeDate: beforeDate ?? undefined,
        sortMethod: TimeEventSortMethod.START_DATETIME,
        sortOrder: SORT_ORDER.DESCENDING,
      })
      .then((r) => {
        setError(undefined);
        setResult({ fetching: false, events: r.data }, 500);
        if (r.data?.length) setSelectedEventId(r.data[0].id);
      })
      .catch((err) => {
        setError(err);
        setResult(prev => ({ ...prev, fetching: false }), 500);
      });
  }, [user, settings.dev, search, dateRange, sdkCtx.client]);

  useEffect(() => {
    loadEvents();
    isReadyRef.current = true;
  }, []);

  if (error) return <ErrorView retry={() => { loadEvents(); }} />;

  return (
    <div className={styles.eventsList}>
      <div className={styles.searchPanel}>
        <div className={styles.searchContainer}>
          <div className={styles.searchItemContainer} style={{ flex: 1 }}>
            Recherche par nom du cours ou de l'expert
            <SearchInput
              setValue={setSearch}
              className={styles.search}
              nativeProps={{
                onKeyDown: (ev) => {
                  if (ev.key === "Enter") {
                    loadEvents();
                  }
                },
                placeholder: "Jean Dupont, Mathématiques ...",
              }}
            />
          </div>
          <div className={styles.searchItemContainer}>
            Recherche par date
            <DatePicker
              className={clsx({
                [styles.datePicker]: true,
                [styles.ready]: dateRange[0],
              })}
              selectsRange={true}
              startDate={dateRange[0] ?? undefined}
              endDate={dateRange[1] ?? undefined}
              onChange={setDateRange}
              isClearable={true}
              placeholderText="Afficher les calendrier"
            />
          </div>
        </div>
        <PrimaryButton
          text={result.fetching ? "Recherche en cours ..." : "Rechercher"}
          className={styles.searchBtn}
          onClick={loadEvents}
        />
      </div>
      <TimeEventsAgenda
        timeEvents={result.events ? result.events.filter((event) => (event.target ? true : false)) : undefined}
        placeholdersNb={3}
        selectedEventId={selectedEventId}
        onSelectEvent={setSelectedEventId}
        onJoinEvent={onPlayVod}
        kind="student"
        mode="replay"
      />
    </div>
  );
};
